import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import QRCodeStyling from "qr-code-styling";
import { messageShow } from "../../common/api/actions";
import routes from "../api/routes";
import {
  Box,
  Toolbar,
  Button,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Autorenew as RegenerateIcon,
  ContentCopy as CopyIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { createOrUpdateQrTable } from "../api/mutation";
import { getQrTableById } from "../api/query";
import { checkPermission } from "../../common/Sidebar/checkPermission";
import "../style.css";

const CreateOrUpdateTable = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const tableId = location.state?.tableId || null;
  const qrDiningAppUrl = location.state?.qrDiningAppUrl || ""; 
  const isEditMode = Boolean(tableId);
  const [tableName, setTableName] = useState("");
  const [email, setEmail] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [qrToken, setQrToken] = useState("");
  const [loginUrl, setLoginUrl] = useState("");
  const [emailError, setEmailError] = useState("");
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);
  const qrCode = useRef(null);
  const [permissions, setPermissions] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
    canCreate: false,
  });
  
  const fetchPermissions = async () => {
    const userPermissions = await checkPermission("store","qrDining");
    setPermissions(userPermissions);
  };

  const regenPassword = () => {
    const newPass = Math.random().toString(36).slice(2, 10);
    setTempPassword(newPass);
  };

  const randomToken = () => {
    const newToken = Math.random().toString().slice(2, 9);
    setQrToken(newToken);
  };

  const handleBack = () => {
    history.push(routes.qrTableList.path);
  };

  useEffect(() => {
    if (isEditMode) {
      const fetchData = async () => {
        try {
          const res = await getQrTableById({ tableId });
          if (res.success && res.data) {
            const data = res.data;
            setTableName(data.tableName);
            setEmail(data.tableUserId?.email);
            setTempPassword("********");
            setQrToken(data.qrToken);
            setLoginUrl(data.loginUrl);
          }
        } catch (err) {
          console.error("Error fetching table details:", err);
          dispatch(messageShow("Failed to load table details."));
        }
      };
      fetchData();
    }
  }, [isEditMode, tableId, dispatch]);

  useEffect(() => {
    fetchPermissions();
  }, [dispatch]);

  useEffect(() => {
    if (qrDiningAppUrl && qrToken) {
      setLoginUrl(`${qrDiningAppUrl}/login?qrtoken=${qrToken}`);
    } else {
      setLoginUrl("");
    }
  }, [qrToken, qrDiningAppUrl]);

  useEffect(() => {
    qrCode.current = new QRCodeStyling({
      width: 150,
      height: 150,
      type: "svg",
      dotsOptions: {
        color: "#000000",
        type: "rounded",
      },
      backgroundOptions: {
        color: "#ffffff",
      },
    });

    if (qrRef.current) {
      qrCode.current.append(qrRef.current);
    }
  }, []);

  useEffect(() => {
    if (qrCode.current && loginUrl) {
      qrCode.current.update({
        data: loginUrl,
      });
    }
  }, [loginUrl]);

  const handleSave = async (e) => {
    e.preventDefault();
    setEmailError("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    try {
      let payload;
      if (isEditMode) {
        payload = {
          _id: tableId,
          tableName,
          email,
        };
      } else {
        payload = {
          tableName,
          email,
          password: tempPassword,
          isActive: true,
          qrToken,
          loginUrl,
        };
      }

      const res = await createOrUpdateQrTable(payload);
    
      if (res.success) {
        dispatch(
          messageShow(
            `QR Table ${isEditMode ? "updated" : "created"} successfully.`
          )
        );
        handleBack();
      } else {
        dispatch(messageShow(res.message || "Something went wrong."));
      }
    } catch (error) {
      console.error("Error saving QR Table:", error);
      dispatch(
        messageShow(
          `Failed to ${
            isEditMode ? "update" : "create"
          } QR Table. Please try again.`
        )
      );
    }
  };

  const handleCopy = () => {
    if (loginUrl) {
      navigator.clipboard.writeText(loginUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // reset after 2s
      });
    }
  };
 
  return (
    <div>
      <Toolbar className="toolbar">
        <IconButton color="inherit" onClick={() => handleBack()}>
          <ArrowBackIcon />
        </IconButton>

        <Typography
          variant="h6"
          color="inherit"
          className="grow"
          sx={{ ml: 1 }}
        >
          {isEditMode ? "Edit Table" : "Add Table"}
        </Typography>
      </Toolbar>

      <form onSubmit={handleSave}>
        <Box className="Wrapper">
          <Box className="rowFlex gap">
            <Box sx={{ flex: 0.6, mr: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Table Name *
              </Typography>
              <TextField
                placeholder="T01"
                fullWidth
                size="small"
                variant="outlined"
                value={tableName}
                required={true}
                onChange={(e) => setTableName(e.target.value)}
                InputLabelProps={{ shrink: false }}
              />
            </Box>
          </Box>

          <Box className="rowFlex gap mt-3">
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Email *
              </Typography>
              <TextField
                placeholder="table+T01@domain.com"
                fullWidth
                size="small"
                variant="outlined"
                type="email"
                value={email}
                required={true}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (
                    emailError &&
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value)
                  ) {
                    setEmailError("");
                  }
                }}
                error={!!emailError}
                helperText={emailError || ""}
                InputLabelProps={{ shrink: false }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Temporary Password *
              </Typography>
              <Box className="rowFlex" sx={{ gap: 1 }}>
                <TextField
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  placeholder="Auto-generate or type"
                  size="small"
                  variant="outlined"
                  required={true}
                  sx={{ flex: 1 }}
                  disabled={isEditMode}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RegenerateIcon />}
                  sx={{ whiteSpace: "nowrap" }}
                  className="blackOutlinedBtn"
                  onClick={regenPassword}
                  disabled={isEditMode}
                >
                  Regenerate
                </Button>
              </Box>
            </Box>
          </Box>

          <Box className="rowFlex gap mt-3">
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                QR Token *
              </Typography>

              <Box className="rowFlex" sx={{ gap: 1 }}>
                <TextField
                  value={qrToken}
                  onChange={(e) => setQrToken(e.target.value)}
                  placeholder="Generate token"
                  size="small"
                  variant="outlined"
                  required={true}
                  sx={{ flex: 1 }}
                  disabled={isEditMode}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RegenerateIcon />}
                  sx={{ whiteSpace: "nowrap" }}
                  className="blackOutlinedBtn"
                  onClick={randomToken}
                  disabled={isEditMode}
                >
                  Generate
                </Button>
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Login URL
              </Typography>
              <Box className="rowFlex" sx={{ gap: 1 }}>
                <TextField
                  value={loginUrl}
                  placeholder="Will appear after token"
                  size="small"
                  variant="outlined"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon />}
                  sx={{ whiteSpace: "nowrap" }}
                  className="blackOutlinedBtn"
                  onClick={handleCopy}
                >
                  {copied ? "Copied!" : "Copy"}
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              QR Code Preview
            </Typography>
            <Box
              className="qrPreview"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <div ref={qrRef} style={{ width: 150, height: 150 }} />

              {!loginUrl ? (
                <Typography variant="body2">
                  Generate a token to preview QR
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ textAlign: "left" }}>
                  This QR links to the login URL with the embedded qrtoken. You
                  can download a larger version after saving.
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="modalActions">
            <Button
              type={"button"}
              aria-label={"Close"}
              variant="outlined"
              // className="cancelBtn"
              color="secondary"
              onClick={() => handleBack()}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              // className="saveBtn" 
              type="submit"  
              aria-label={"Save"}
              color={"primary"}
              disabled={!permissions?.canEdit}
            >
              {isEditMode ? "Update" : "Save"}
            </Button>
          </Box>
        </Box>
      </form>
    </div>
  );
};

export default CreateOrUpdateTable;
